import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  InlineLoading,
  RadioButton,
  RadioButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tile,
  Toggle,
} from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import { formatNumber } from 'utils/number';
import { CarryChart } from './carry-chart';
import { fmtMonth, fmtUsd } from './interest-modeling-format';
import {
  buildChartModel,
  buildSummaryRows,
  colorForIndex,
  isCarryItemComplete,
  portfolioAggregateExcluding,
  portfolioKpis,
  type CachedTract,
  type ChartView,
  type SummaryDirection,
  type SummaryTract,
} from './interest-modeling-preview';
import type { CarryPreview } from './use-carry-preview';
import type { CarryGroup, CarryWell, InterestItem } from './types';
import styles from './carry-portfolio.module.scss';

const VIEW_OPTIONS: { value: ChartView; label: string }[] = [
  { value: 'aggregate', label: 'Aggregate' },
  { value: 'byAsset', label: 'By asset' },
  { value: 'byProperty', label: 'By property' },
];

// An excluded row shows its own values as em-dashes (the totals it fed have moved to the
// chart + KPIs); its Properties count stays so you can still read what it would re-add.
const EXCLUDED_CELL = '\u2014';

const DIRECTION_LABEL: Record<SummaryDirection, string> = {
  carrying: 'Carrying',
  carried: 'Carried',
  working: 'Working interest (full participation)',
};

export interface CarryPortfolioProps {
  /** The live preview session (portfolio + per-tract cache + status + actions). */
  preview: CarryPreview;
  /** The active chart mode + its setter (the RadioButtonGroup). */
  view: ChartView;
  onViewChange: (view: ChartView) => void;
  /** The tract the content area is focused on (scopes the byAsset / byProperty views). */
  activeTractId: string;
  /** All interest items (carry ones drive the portfolio; elections are ignored here). */
  interestItems: InterestItem[];
  /** Resolves a tract id to its display name (for the "Asset · Group" labels). */
  nameFor: (tractId: string) => string;
  /** A tract's "(N of M Subtracts)" when the package holds it in part, else null. */
  coverageFor: (tractId: string) => string | null;
  /** Each tract's well roster (total-well counts + byProperty well names). */
  wellsByTract: ReadonlyMap<string, CarryWell[]>;
}

/**
 * The carried-interest portfolio surface: three KPI
 * tiles (breakeven / net-to-us / peak exposure) from the recombined portfolio, a
 * SciChart chart with an Aggregate / By asset / By property toggle, and a per-group
 * summary table — all reading the live preview state. Pure derivations
 * (`portfolioKpis` / `buildChartModel` / `buildSummaryRows`) turn the server payloads
 * into the rendered shapes; the surface never blocks (a preparing / warming state shows
 * while the frame builds, and a soft error keeps the last-good preview).
 *
 * The KPI shell + summary table render as soon as any carry group exists — the table is
 * driven by the CLIENT's groups (placeholder cells until the server prices them), so a
 * group has a row before its first reprice lands and even when the server omits it
 * (all of its wells priced at zero). Only the chart waits for a recombined portfolio;
 * until then its slot shows the non-blocking state.
 *
 * @param {CarryPortfolioProps} props - The preview session, view state, and tract context.
 * @return {JSX.Element} The carry portfolio surface.
 */
export function CarryPortfolio({
  preview,
  view,
  onViewChange,
  activeTractId,
  interestItems,
  nameFor,
  coverageFor,
  wellsByTract,
}: CarryPortfolioProps) {
  const { portfolio, cache, status, repricingTractIds } = preview;

  const carryItems = useMemo(
    () => interestItems.filter((item) => item.kind === 'carry'),
    [interestItems]
  );

  // Every carry tract as the client knows it (live groups + roster size), with its cached
  // server result once priced. Interest-item order, so the flattened group order (and
  // thus the shared color index) is stable across the chart + summary table.
  const carryTracts = useMemo<SummaryTract[]>(
    () =>
      carryItems.map((item) => {
        const roster = wellsByTract.get(item.tractId) ?? [];
        const groups = item.groups ?? [];
        const assigned = new Set(groups.flatMap((group) => group.wellIds));
        return {
          tractId: item.tractId,
          name: nameFor(item.tractId),
          coverage: coverageFor(item.tractId),
          totalWells: roster.length || assigned.size,
          groups,
          result: cache.get(item.tractId),
        };
      }),
    [carryItems, cache, wellsByTract, nameFor, coverageFor]
  );
  // The chart reads only the priced tracts — filtered from the same list, so both
  // derivations walk the same flattened group order.
  const cachedTracts = useMemo<CachedTract[]>(
    () =>
      carryTracts.filter(
        (tract): tract is Required<SummaryTract> => tract.result !== undefined
      ),
    [carryTracts]
  );

  const wellNames = useMemo(() => {
    const map = new Map<string, string>();
    wellsByTract.forEach((wells) =>
      wells.forEach((well) => map.set(well.id, well.name))
    );
    return map;
  }, [wellsByTract]);

  const clientGroupsById = useMemo(() => {
    const map = new Map<string, CarryGroup>();
    carryItems.forEach((item) =>
      (item.groups ?? []).forEach((group) => map.set(group.id, group))
    );
    return map;
  }, [carryItems]);

  // Rows the user has toggled off — view-time only (UI-only what-if). Keyed by summary row
  // id (`tractId:groupId`); the machinery is row-agnostic though only the full-WI row shows
  // a toggle today. Never reprices, never affects Calculate.
  const [excludedRowIds, setExcludedRowIds] = useState<Set<string>>(
    () => new Set()
  );
  const setRowIncluded = useCallback((id: string, included: boolean) => {
    setExcludedRowIds((prev) => {
      const next = new Set(prev);
      if (included) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);
  // The portfolio the chart + KPIs actually draw: the recombined aggregate less the excluded
  // rows' cached series. Identical to `portfolio` when nothing is excluded.
  const effectivePortfolio = useMemo(
    () => portfolioAggregateExcluding(portfolio, excludedRowIds, carryTracts),
    [portfolio, excludedRowIds, carryTracts]
  );

  const kpis = useMemo(
    () => portfolioKpis(effectivePortfolio),
    [effectivePortfolio]
  );
  const chartModel = useMemo(
    () =>
      buildChartModel(
        effectivePortfolio,
        cachedTracts,
        wellNames,
        clientGroupsById,
        excludedRowIds
      ),
    [
      effectivePortfolio,
      cachedTracts,
      wellNames,
      clientGroupsById,
      excludedRowIds,
    ]
  );
  const summaryRows = useMemo(
    () => buildSummaryRows(carryTracts, portfolio),
    [carryTracts, portfolio]
  );
  const hasGroups = carryItems.some((item) => (item.groups ?? []).length > 0);
  // The busy indicator reads "Repricing…", so it only shows once there is a portfolio to
  // reprice — the first pricing is announced by the chart slot's own state.
  const repricing = portfolio.length > 0 && repricingTractIds.size > 0;
  const onRetry = () =>
    retryActive(preview, interestItems, activeTractId, view);

  if (!hasGroups) {
    return (
      <div className={styles.state}>
        {renderEmptyState(status, carryItems, onRetry)}
      </div>
    );
  }

  return (
    <div className={styles.portfolio}>
      <div className={styles.topBar}>
        <div className={styles.kpis}>
          <Tile className={styles.kpi}>
            <span className={styles.kpiValue}>
              {fmtMonth(kpis.breakevenMonth)}
            </span>
            <span className={styles.kpiLabel}>Portfolio breakeven</span>
          </Tile>
          <Tile className={styles.kpi}>
            <span className={kpiValueClass(kpis.netToUs)}>
              {fmtUsd(kpis.netToUs)}
            </span>
            <span className={styles.kpiLabel}>Net to us</span>
          </Tile>
          <Tile className={styles.kpi}>
            <span className={styles.kpiValue}>{fmtUsd(kpis.peakExposure)}</span>
            <span className={styles.kpiLabel}>Peak exposure</span>
          </Tile>
        </div>
        <div className={styles.viewControls}>
          {repricing && (
            <InlineLoading description="Repricing…" status="active" />
          )}
          <RadioButtonGroup
            name="carry-portfolio-view"
            aria-label="Chart view"
            valueSelected={view}
            onChange={(value) => onViewChange(value as ChartView)}
          >
            {VIEW_OPTIONS.map((option) => (
              <RadioButton
                key={option.value}
                labelText={option.label}
                value={option.value}
              />
            ))}
          </RadioButtonGroup>
        </div>
      </div>

      {/* The stale note only makes sense over a last-good preview; with no portfolio
        yet, the chart slot's error state carries the retry instead. */}
      {status === 'error' && portfolio.length > 0 && (
        <div className={styles.staleNote}>
          <span>Preview may be out of date.</span>
          <Button kind="ghost" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </div>
      )}

      {portfolio.length > 0 ? (
        <CarryChart
          model={chartModel}
          view={view}
          focusTractId={activeTractId}
          isLoading={status === 'preparing'}
        />
      ) : (
        <div className={styles.state}>
          {renderEmptyState(status, carryItems, onRetry)}
        </div>
      )}

      <div className={styles.summaryWrap}>
        <Table size="sm" className={styles.summary}>
          <TableHead>
            <TableRow>
              <TableHeader>Asset · Group</TableHeader>
              <TableHeader>Direction</TableHeader>
              <TableHeader>Properties</TableHeader>
              <TableHeader className={styles.num}>Payout</TableHeader>
              <TableHeader className={styles.num}>Net to us</TableHeader>
              <TableHeader className={styles.num}>Multiple</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaryRows.map((row) => {
              const excluded = excludedRowIds.has(row.id);
              // Only the full-WI row is toggleable today; the exclusion machinery is
              // row-agnostic, so widening this predicate lights it up for other rows.
              const toggleable = row.direction === 'working';
              const rowClass =
                [
                  repricingTractIds.has(row.tractId) ? styles.repricingRow : '',
                  excluded ? styles.excludedRow : '',
                ]
                  .filter(Boolean)
                  .join(' ') || undefined;
              return (
                <TableRow
                  key={row.id}
                  className={rowClass}
                  aria-busy={repricingTractIds.has(row.tractId)}
                >
                  <TableCell>
                    <span className={styles.groupCell}>
                      {renderColorDot(row.colorIndex)}
                      {row.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={styles.directionCell}>
                      {DIRECTION_LABEL[row.direction]}
                      {toggleable && (
                        <span className={styles.rowToggle}>
                          <Toggle
                            id={`carry-include-${row.id}`}
                            size="sm"
                            hideLabel
                            aria-label={`Include ${row.label} in the modeled totals`}
                            labelA="Excluded"
                            labelB="Included"
                            toggled={!excluded}
                            onToggle={(checked) =>
                              setRowIncluded(row.id, checked)
                            }
                          />
                          <span className={styles.toggleState}>
                            {excluded ? 'Excluded' : 'Included'}
                          </span>
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <FormattedCount value={row.wellCount} />/
                    <FormattedCount value={row.totalWells} />
                  </TableCell>
                  <TableCell className={styles.num}>
                    {excluded ? EXCLUDED_CELL : fmtMonth(row.payoutMonth)}
                  </TableCell>
                  <TableCell
                    className={
                      excluded
                        ? styles.num
                        : `${styles.num} ${kpiValueClass(row.netToUs)}`
                    }
                  >
                    {excluded ? EXCLUDED_CELL : fmtUsd(row.netToUs)}
                  </TableCell>
                  <TableCell className={styles.num}>
                    {excluded ? (
                      EXCLUDED_CELL
                    ) : (
                      <>
                        {formatNumber({
                          value: row.multiple,
                          minPrecision: 2,
                          maxPrecision: 2,
                        })}
                        ×
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// The Net-to-us value class — green when non-negative, red when a loss.
function kpiValueClass(value: number): string {
  return `${styles.kpiValue} ${value >= 0 ? styles.positive : styles.negative}`;
}

// The row's color dot: the chart band's color once the group is priced, else neutral.
function renderColorDot(colorIndex: number | null) {
  if (colorIndex === null) {
    // The gray dot is the only cue that "$0" is a placeholder, so name it for readers.
    return (
      <span
        className={`${styles.colorDot} ${styles.colorDotPending}`}
        role="img"
        aria-label="Not yet priced"
      />
    );
  }
  return (
    <span
      className={styles.colorDot}
      style={{ background: colorForIndex(colorIndex) }}
    />
  );
}

// Re-fire the active tract's reprice (the quiet retry after a soft error).
function retryActive(
  preview: CarryPreview,
  interestItems: InterestItem[],
  activeTractId: string,
  view: ChartView
) {
  const active = interestItems.find(
    (item) => item.tractId === activeTractId && item.kind === 'carry'
  );
  if (active) {
    preview.reprice(active, view);
  }
}

// The non-blocking state shown while the portfolio has no data yet.
function renderEmptyState(
  status: CarryPreview['status'],
  carryItems: InterestItem[],
  onRetry: () => void
) {
  if (status === 'preparing') {
    return <InlineLoading description="Preparing preview…" status="active" />;
  }
  if (status === 'needs_recalculation') {
    return (
      <span className={styles.stateHint}>
        Recalculate to preview interest modeling on this evaluation.
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className={styles.stateBody}>
        <span className={styles.stateHint}>
          Preview unavailable right now — it won’t block editing.
        </span>
        <Button kind="ghost" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </span>
    );
  }
  if (carryItems.some(isCarryItemComplete)) {
    return (
      <InlineLoading description="Modeling carry portfolio…" status="active" />
    );
  }
  return (
    <span className={styles.stateHint}>
      Assign properties to a group and set the carry terms to see the portfolio.
    </span>
  );
}
